import Skeleton from '@mui/material/Skeleton';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppContext } from 'context/AppProvider';
import { MenuSlot } from 'components/styles/SlotStyles';
import { PageWrapperDesktop, PageWrapperMobile } from 'components/styles/PageWrapper';
import { getDefaultLayout, getGridSizes, isFullHeightLayout } from 'components/GridLayout/helper';
import type { LayoutOptions } from 'components/GridLayout';

// The default skeleton tint assumes a light background; match each surface's text colour instead
const tint = (surface: 'primary' | 'secondary' | 'tertiary') => ({
  bgcolor: (theme: Theme) => alpha(theme.palette[surface].contrastText, 0.15),
});

type ContentProps = {
  /** Number of charts the opened results menu will show; the system config knows it before any data arrives */
  chartCount?: number;
  /** The menu's default layout, if any; resolved the same way ResultsPlotComponent does */
  defaultLayout?: LayoutOptions;
};

/**
 * Placeholder for the results content while simulation data loads. Header, timeline and one tile per chart,
 * arranged with the same grid sizes GridLayout will use, so nothing jumps when the real charts appear.
 */
export const ResultsContentSkeleton = ({ chartCount = 4, defaultLayout }: ContentProps) => {
  const count = Math.max(chartCount, 1);
  const layout = getDefaultLayout(count, defaultLayout);
  const sizes = getGridSizes(layout, count);
  const fullHeight = isFullHeightLayout(layout);
  return (
    <div className='flex h-full flex-col gap-4 bg-tertiary-main p-4' data-test-id='results-skeleton'>
      <Skeleton variant='text' width='30%' height={48} sx={tint('tertiary')} />
      <Skeleton variant='rounded' height={32} sx={tint('tertiary')} />
      <div className='grid flex-1 auto-rows-fr grid-cols-12 gap-4'>
        {sizes.map((size, i) => (
          <Skeleton
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            variant='rounded'
            height='100%'
            sx={{ ...tint('tertiary'), gridColumn: `span ${size}`, minHeight: fullHeight ? '100%' : '40%' }}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Whole-page placeholder while the results route chunk loads. The menu column stays empty on purpose:
 * its entries come from the system config and render as soon as the page itself does.
 */
const ResultsSkeleton = () => {
  const theme = useTheme();
  const { menuIsCollapsedByDefault } = useAppContext();
  const mobileActive = useMediaQuery(theme.breakpoints.down('lg'));
  const portraitMode = useMediaQuery('(orientation: portrait)');

  if (mobileActive && portraitMode) {
    return (
      <PageWrapperMobile>
        <Skeleton variant='rectangular' height={56} sx={tint('secondary')} />
        <div className='flex-1'>
          <ResultsContentSkeleton />
        </div>
        <Skeleton variant='rectangular' height={56} sx={tint('secondary')} />
      </PageWrapperMobile>
    );
  }

  return (
    <PageWrapperDesktop>
      <MenuSlot $isCollapsed={menuIsCollapsedByDefault} />
      <div className='flex-1'>
        <ResultsContentSkeleton />
      </div>
    </PageWrapperDesktop>
  );
};

export default ResultsSkeleton;
